import { E2EPage, newE2EPage } from "@stencil/core/testing";

declare const global;

describe("uic-dialog", () => {
    /* eslint-disable no-undef */
    global.MutationObserver = class {
        constructor() {}

        disconnect() {}

        observe() {}

        takeRecords(): MutationRecord[] {
            return [];
        }
    };

    // some helpers used to slightly reduce code complexity in tests below
    const getPage = async (html) => newE2EPage({ html: `<div class="uic-theme-responsive">${html}</div>` });
    const getDismissIcon = (page) => page.find(".uic-dialog__panel-header-close-button");
    const getBackground = (page) => page.find(".uic-dialog__background");
    const setDismissable = (page, state) =>
        page.find("uic-dialog").then((d) => {
            d.setProperty("dismissable", state);
        });
    const setBackgroundDismissable = (page, state) =>
        page.find("uic-dialog").then((d) => {
            d.setProperty("backgroundDismissable", state);
        });

    it("renders", async () => {
        const page = await newE2EPage();
        await page.setContent("<uic-dialog></uic-dialog>");

        const element = await page.find("uic-dialog");
        expect(element).toHaveClass("hydrated");
    });

    it("has no visible footer by default", async () => {
        const page = await newE2EPage();
        await page.setContent("<uic-dialog></uic-dialog>");

        const footer = await page.find("uic-dialog uic-footer");
        expect(footer).toHaveClass("uic-hidden");
    });

    it("has no visible footer when adding button to content", async () => {
        const page = await newE2EPage();
        await page.setContent("<uic-dialog><uic-button></uic-button></uic-dialog>");

        const footer = await page.find("uic-dialog uic-footer");
        expect(footer).toHaveClass("uic-hidden");
    });

    it("has visible footer as soon as there is one regular footer button", async () => {
        const page = await newE2EPage();
        await page.setContent("<uic-dialog><uic-button slot='footer-button'></uic-button></uic-dialog>");

        const footer = await page.find("uic-dialog uic-footer");
        expect(footer).not.toHaveClass("uic-hidden");
    });

    it("has visible footer as soon as there are multiple regular footer buttons", async () => {
        const page = await newE2EPage();
        await page.setContent(
            "<uic-dialog><uic-button slot='footer-button'></uic-button><uic-button slot='footer-button'></uic-button></uic-dialog>"
        );

        const footer = await page.find("uic-dialog uic-footer");
        expect(footer).not.toHaveClass("uic-hidden");
    });

    it("has visible footer as soon as there is one left-aligned footer button", async () => {
        const page = await newE2EPage();
        await page.setContent("<uic-dialog><uic-button slot='footer-button-secondary'></uic-button></uic-dialog>");

        const footer = await page.find("uic-dialog uic-footer");
        expect(footer).not.toHaveClass("uic-hidden");
    });

    it("has visible footer as soon as there are multiple left-aligned footer buttons", async () => {
        const page = await newE2EPage();
        await page.setContent(
            // eslint-disable-next-line
            "<uic-dialog><uic-button slot='footer-button-secondary'></uic-button><uic-button slot='footer-button-secondary'></uic-button></uic-dialog>"
        );

        const footer = await page.find("uic-dialog uic-footer");
        expect(footer).not.toHaveClass("uic-hidden");
    });

    it("has visible footer as soon as there is one regular and a secondary footer button", async () => {
        const page = await newE2EPage();
        await page.setContent(
            // eslint-disable-next-line
            "<uic-dialog><uic-button slot='footer-button'></uic-button><uic-button slot='footer-button-secondary'></uic-button></uic-dialog>"
        );

        const footer = await page.find("uic-dialog uic-footer");
        expect(footer).not.toHaveClass("uic-hidden");
    });

    it("has visible footer as soon as there are multiple regular and secondary footer buttons", async () => {
        const page = await newE2EPage();
        await page.setContent(
            // eslint-disable-next-line
            "<uic-dialog><uic-button slot='footer-button'></uic-button><uic-button slot='footer-button'></uic-button><uic-button slot='footer-button-secondary'></uic-button><uic-button slot='footer-button-secondary'></uic-button></uic-dialog>"
        );

        const footer = await page.find("uic-dialog uic-footer");
        expect(footer).not.toHaveClass("uic-hidden");
    });

    it("puts regular footer button into second slot of footer", async () => {
        const page = await newE2EPage();
        await page.setContent("<uic-dialog><uic-button slot='footer-button'></uic-button></uic-dialog>");

        const [left, right] = await page.findAll("uic-dialog uic-footer .uic-footer__container");

        const leftButtons = await left.findAll("uic-button");
        const rightButtons = await right.findAll("uic-button");

        expect(leftButtons).toHaveLength(0);
        expect(rightButtons).toHaveLength(1);
    });

    it("puts regular footer buttons into second slot of footer", async () => {
        const page = await newE2EPage();
        await page.setContent(
            "<uic-dialog><uic-button slot='footer-button'></uic-button><uic-button slot='footer-button'></uic-button></uic-dialog>"
        );

        const [left, right] = await page.findAll("uic-dialog uic-footer .uic-footer__container");

        const leftButtons = await left.findAll("uic-button");
        const rightButtons = await right.findAll("uic-button");

        expect(leftButtons).toHaveLength(0);
        expect(rightButtons).toHaveLength(2);
    });

    it("puts secondary footer button into first slot of footer", async () => {
        const page = await newE2EPage();
        await page.setContent("<uic-dialog><uic-button slot='footer-button-secondary'></uic-button></uic-dialog>");

        const [left, right] = await page.findAll("uic-dialog uic-footer .uic-footer__container");

        const leftButtons = await left.findAll("uic-button");
        const rightButtons = await right.findAll("uic-button");

        expect(leftButtons).toHaveLength(1);
        expect(rightButtons).toHaveLength(0);
    });

    it("puts secondary footer buttons into first slot of footer", async () => {
        const page = await newE2EPage();
        await page.setContent(
            // eslint-disable-next-line
            "<uic-dialog><uic-button slot='footer-button-secondary'></uic-button><uic-button slot='footer-button-secondary'></uic-button></uic-dialog>"
        );

        const [left, right] = await page.findAll("uic-dialog uic-footer .uic-footer__container");

        const leftButtons = await left.findAll("uic-button");
        const rightButtons = await right.findAll("uic-button");

        expect(leftButtons).toHaveLength(2);
        expect(rightButtons).toHaveLength(0);
    });

    describe("X icon for dismissing dialog", () => {
        describe("is available", () => {
            const check = async (page) => {
                const dismissIcon = await getDismissIcon(page);

                expect(dismissIcon).toBeTruthy();
            };

            it("by default", async () => {
                await check(await getPage(`<uic-dialog is-open></uic-dialog>`));
            });

            it("by default while attribute `background-dismissable` is false", async () => {
                const page = await getPage(`<uic-dialog is-open background-dismissable="false"></uic-dialog>`);

                await setBackgroundDismissable(page, false);
                await page.waitForChanges();
                await check(page);
            });

            it("by default while property `backgroundDismissable` is false", async () => {
                const page = await getPage(`<uic-dialog is-open></uic-dialog>`);

                await setBackgroundDismissable(page, false);
                await page.waitForChanges();

                await check(page);
            });

            it("if attribute `dismissable` is true", async () => {
                await check(await getPage(`<uic-dialog is-open dismissable></uic-dialog>`));
            });

            it("if property `dismissable` is true", async () => {
                const page = await getPage(`<uic-dialog is-open></uic-dialog>`);

                await setDismissable(page, true);
                await page.waitForChanges();

                await check(page);
            });

            it("if attribute `dismissable` is true while attribute `background-dismissable` is false", async () => {
                await check(await getPage(`<uic-dialog is-open dismissable background-dismissable="false"></uic-dialog>`));
            });

            it("if attribute `dismissable` is true while property `backgroundDismissable` is false", async () => {
                const page = await getPage(`<uic-dialog is-open dismissable></uic-dialog>`);

                await setBackgroundDismissable(page, false);
                await page.waitForChanges();

                await check(page);
            });

            it("if attribute `dismissable` is true while attribute `background-dismissable` is true", async () => {
                await check(await getPage(`<uic-dialog is-open dismissable background-dismissable></uic-dialog>`));
            });

            it("if attribute `dismissable` is true while property `backgroundDismissable` is true", async () => {
                const page = await getPage(`<uic-dialog is-open dismissable></uic-dialog>`);

                await setBackgroundDismissable(page, true);
                await page.waitForChanges();

                await check(page);
            });

            it("if property `dismissable` is true while attribute `background-dismissable` is false", async () => {
                const page = await getPage(`<uic-dialog is-open background-dismissable="false"></uic-dialog>`);

                await setDismissable(page, true);
                await page.waitForChanges();

                await check(page);
            });

            it("if property `dismissable` is true while property `backgroundDismissable` is false", async () => {
                const page = await getPage(`<uic-dialog is-open></uic-dialog>`);

                await setDismissable(page, true);
                await setBackgroundDismissable(page, false);
                await page.waitForChanges();

                await check(page);
            });

            it("if property `dismissable` is true while attribute `background-dismissable` is true", async () => {
                const page = await getPage(`<uic-dialog is-open background-dismissable></uic-dialog>`);

                await setDismissable(page, true);
                await page.waitForChanges();

                await check(page);
            });

            it("if property `dismissable` is true while property `backgroundDismissable` is true", async () => {
                const page = await getPage(`<uic-dialog is-open></uic-dialog>`);

                await setDismissable(page, true);
                await setBackgroundDismissable(page, true);
                await page.waitForChanges();

                await check(page);
            });
        });

        describe("is missing", () => {
            const check = async (page) => {
                const dismissIcon = await getDismissIcon(page);

                expect(dismissIcon).toBeFalsy();
            };

            it("if attribute `dismissable` is false", async () => {
                await check(await getPage(`<uic-dialog is-open dismissable="false"></uic-dialog>`));
            });

            it("if property `dismissable` is false", async () => {
                const page = await getPage(`<uic-dialog is-open></uic-dialog>`);

                await setDismissable(page, false);
                await page.waitForChanges();

                await check(page);
            });

            it("if attribute `dismissable` is false while attribute `background-dismissable` is true", async () => {
                await check(await getPage(`<uic-dialog is-open dismissable="false" background-dismissable></uic-dialog>`));
            });

            it("if attribute `dismissable` is false while property `backgroundDismissable` is true", async () => {
                const page = await getPage(`<uic-dialog is-open dismissable="false"></uic-dialog>`);

                await setBackgroundDismissable(page, true);
                await page.waitForChanges();

                await check(page);
            });

            it("if property `dismissable` is false while attribute `background-dismissable` is true", async () => {
                const page = await getPage(`<uic-dialog is-open background-dismissable></uic-dialog>`);

                await setDismissable(page, false);
                await page.waitForChanges();

                await check(page);
            });

            it("if property `dismissable` is false while property `backgroundDismissable` is true", async () => {
                const page = await getPage(`<uic-dialog is-open></uic-dialog>`);

                await setDismissable(page, false);
                await setBackgroundDismissable(page, true);
                await page.waitForChanges();

                await check(page);
            });

            it("if attribute `dismissable` is false while attribute `background-dismissable` is false", async () => {
                await check(await getPage(`<uic-dialog is-open dismissable="false" background-dismissable="false"></uic-dialog>`));
            });

            it("if attribute `dismissable` is false while property `backgroundDismissable` is false", async () => {
                const page = await getPage(`<uic-dialog is-open dismissable="false"></uic-dialog>`);

                await setBackgroundDismissable(page, false);
                await page.waitForChanges();

                await check(page);
            });

            it("if property `dismissable` is false while attribute `background-dismissable` is false", async () => {
                const page = await getPage(`<uic-dialog is-open background-dismissable="false"></uic-dialog>`);

                await setDismissable(page, false);
                await page.waitForChanges();

                await check(page);
            });

            it("if property `dismissable` is false while property `backgroundDismissable` is false", async () => {
                const page = await getPage(`<uic-dialog is-open></uic-dialog>`);

                await setDismissable(page, false);
                await setBackgroundDismissable(page, false);
                await page.waitForChanges();

                await check(page);
            });
        });
    });

    describe("clicking", () => {
        describe("on button in content", () => {
            it("does not dismiss dialog by default", async () => {
                const page = await getPage(`<uic-dialog is-open><uic-button class="foo">OK</uic-button></uic-dialog>`);

                const event = await page.spyOnEvent("uicClose");

                await page.click(".foo");
                await page.waitForChanges();

                expect(event).toHaveReceivedEventTimes(0);
            });

            it("does not dismiss dialog if attribute `uic-close-dialog-button` is set", async () => {
                const page = await getPage(
                    `<uic-dialog is-open uic-close-dialog-button><uic-button class="foo">OK</uic-button></uic-dialog>`
                );

                const event = await page.spyOnEvent("uicClose");

                await page.click(".foo");
                await page.waitForChanges();

                expect(event).toHaveReceivedEventTimes(0);
            });

            it("click event is propagated outside the component", async () => {
                const page = await getPage(`<uic-dialog is-open><uic-button class="foo">OK</uic-button></uic-dialog>`);
                const event = await page.spyOnEvent("click");

                await page.click(".foo");
                await page.waitForChanges();

                expect(event).toHaveReceivedEventTimes(1);
            });
        });

        describe("on button in footer", () => {
            it("does not dismiss dialog by default", async () => {
                const page = await getPage(`<uic-dialog is-open><uic-button slot="footer-button">OK</uic-button></uic-dialog>`);

                const event = await page.spyOnEvent("uicClose");

                await page.click('[slot="footer-button"]');
                await page.waitForChanges();

                expect(event).toHaveReceivedEventTimes(0);
            });

            it("dismisses dialog if attribute `uic-close-dialog-button` is set", async () => {
                const page = await getPage(
                    `<uic-dialog is-open><uic-button slot="footer-button" uic-close-dialog-button>OK</uic-button></uic-dialog>`
                );

                const event = await page.spyOnEvent("uicClose");

                await page.click('[slot="footer-button"]');
                await page.waitForChanges();

                expect(event).toHaveReceivedEventTimes(1);
                expect(event).toHaveReceivedEventDetail({
                    trigger: "footer-primary"
                });
            });

            it("click event is propagated outside the component", async () => {
                const page = await getPage(`<uic-dialog is-open><uic-button slot="footer-button">OK</uic-button></uic-dialog>`);
                const event = await page.spyOnEvent("click");

                await page.click('[slot="footer-button"]');
                await page.waitForChanges();

                expect(event).toHaveReceivedEventTimes(1);
            });
        });

        describe("on X icon", () => {
            describe("dismisses dialog and notifies its container", () => {
                const check = async (page) => {
                    const closeEvent = await page.spyOnEvent("uicClose");
                    const dismissIcon = await getDismissIcon(page);

                    await dismissIcon.click();
                    await page.waitForChanges();

                    expect(closeEvent).toHaveReceivedEventTimes(1);
                    expect(closeEvent).toHaveReceivedEventDetail({
                        trigger: "close-button"
                    });
                };

                it("by default", async () => {
                    await check(await getPage(`<uic-dialog is-open></uic-dialog>`));
                });

                it("if attribute `background-dismissable` is set false", async () => {
                    await check(await getPage(`<uic-dialog is-open background-dismissable="false"></uic-dialog>`));
                });

                it("if property `backgroundDismissable` is set false", async () => {
                    const page = await getPage(`<uic-dialog is-open></uic-dialog>`);

                    await setBackgroundDismissable(page, false);
                    await page.waitForChanges();

                    await check(page);
                });
            });
        });

        describe("on background", () => {
            describe("dismisses dialog and notifies its container", () => {
                const check = async (page) => {
                    const background = await getBackground(page);

                    const closeEvent = await page.spyOnEvent("uicClose");
                    const bgClickEvent = await background.spyOnEvent("click");
                    const windowClickEvent = await page.spyOnEvent("click");

                    await page.mouse.click(10, 100);
                    await page.waitForChanges();

                    expect(bgClickEvent).toHaveReceivedEventTimes(1);
                    expect(closeEvent).toHaveReceivedEventTimes(1);
                    expect(closeEvent).toHaveReceivedEventDetail({
                        trigger: "background"
                    });
                    expect(windowClickEvent).toHaveReceivedEventTimes(0);
                };

                it("by default", async () => {
                    await check(await getPage(`<uic-dialog is-open></uic-dialog>`));
                });

                it("if attribute `background-dismissable` is set true", async () => {
                    await check(await getPage(`<uic-dialog is-open background-dismissable></uic-dialog>`));
                });

                it("if attribute `dismissable` is set true", async () => {
                    await check(await getPage(`<uic-dialog is-open dismissable></uic-dialog>`));
                });

                it("if attribute `dismissable` is set false, but background-dismissable is set true", async () => {
                    await check(await getPage(`<uic-dialog is-open dismissable="false" background-dismissable></uic-dialog>`));
                });
            });

            describe("neither dismisses dialog nor notifies its container", () => {
                const check = async (page) => {
                    const background = await getBackground(page);

                    const closeEvent = await page.spyOnEvent("uicClose");
                    const bgClickEvent = await background.spyOnEvent("click");
                    const windowClickEvent = await page.spyOnEvent("click");

                    await page.mouse.click(10, 100);
                    await page.waitForChanges();

                    expect(bgClickEvent).toHaveReceivedEventTimes(1);
                    expect(closeEvent).toHaveReceivedEventTimes(0);
                    expect(windowClickEvent).toHaveReceivedEventTimes(0);
                };

                it("if attribute `background-dismissable` is set false", async () => {
                    await check(await getPage(`<uic-dialog is-open background-dismissable="false"></uic-dialog>`));
                });

                it("if attribute `dismissable` is set false", async () => {
                    await check(await getPage(`<uic-dialog is-open dismissable="false">Foo</uic-dialog>`));
                });

                it("if attribute `dismissable` is set true, but background-dismissable is set false", async () => {
                    await check(await getPage(`<uic-dialog is-open dismissable background-dismissable="false"></uic-dialog>`));
                });
            });
        });

        xdescribe("on background control", () => {
            const check = async (isAllowed: boolean, page: E2EPage) => {
                const backgroundButton = await page.find("#test > button");
                const background = await getBackground(page);

                const closeEvent = await page.spyOnEvent("uicClose");
                const bgButtonClickEvent = await backgroundButton.spyOnEvent("click");
                const bgClickEvent = await background.spyOnEvent("click");
                await backgroundButton.click();
                await page.waitForChanges();

                expect(bgButtonClickEvent).toHaveReceivedEventTimes(isAllowed ? 1 : 0);
                expect(bgClickEvent).toHaveReceivedEventTimes(isAllowed ? 0 : 1);
                expect(closeEvent).toHaveReceivedEventTimes(0);
            };

            it("is not allowed by default", async () => {
                await check(
                    false,
                    await getPage(
                        `<uic-button id="test" label="Background test button" style="top: 100px;"></uic-button>
                  <uic-dialog is-open dismissable="false"></uic-dialog>`
                    )
                );
            });

            it("is allowed if attribute `allowBackdropClick` is set true", async () => {
                await check(
                    true,
                    await getPage(
                        `<uic-button id="test" label="Background test button" style="top: 100px;"></uic-button>
                    <uic-dialog is-open allow-backdrop-click></uic-dialog>`
                    )
                );
            });
        });
    });
});
